import { useState } from "react";
import type { Dialog } from "@/types/dialog";
import { getLanguageConfig } from "@/lib/language-config";
import { getLanguageName } from "@/lib/language-names";
import { useDialogStore } from "./dialog-store";
import { useLineAvatar } from "./use-line-avatar";

interface DialogHeaderProps {
  dialog: Dialog;
}

const LEVEL_GRADIENTS: Record<string, string> = {
  A1: "from-emerald-600 to-teal-700",
  A2: "from-green-600 to-emerald-700",
  B1: "from-amber-500 to-orange-600",
  B2: "from-orange-500 to-red-600",
  C1: "from-purple-600 to-violet-700",
  C2: "from-indigo-600 to-blue-700",
};

const LEVEL_BADGES: Record<string, string> = {
  A1: "bg-emerald-500/30 text-emerald-300 border-emerald-500/30",
  A2: "bg-green-500/30 text-green-300 border-green-500/30",
  B1: "bg-amber-500/30 text-amber-300 border-amber-500/30",
  B2: "bg-orange-500/30 text-orange-300 border-orange-500/30",
  C1: "bg-purple-500/30 text-purple-300 border-purple-500/30",
  C2: "bg-indigo-500/30 text-indigo-300 border-indigo-500/30",
};

function SpeakerBubble({ dialog, speakerIndex }: { dialog: Dialog; speakerIndex: number }) {
  const speaker = dialog.speakers[speakerIndex];
  const thumbnailUrl = dialog.speaker_thumbnails?.[String(speakerIndex)]?.url ?? null;
  const avatarUrl = useLineAvatar(
    dialog.id,
    dialog.language_code,
    speakerIndex,
    "neutral",
    speaker?.avatarBase ?? null,
  );
  const [imgError, setImgError] = useState(false);

  if (!speaker) return null;

  const imgSrc = thumbnailUrl ?? avatarUrl;

  return (
    <div className="flex flex-col items-center gap-1">
      <div className="w-14 h-14 rounded-full overflow-hidden ring-2 ring-white/20">
        {imgSrc && !imgError ? (
          <img
            src={imgSrc}
            alt={speaker.name}
            className="w-full h-full object-cover"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className={`w-full h-full flex items-center justify-center text-lg font-bold ${
            speaker.gender === "male" ? "bg-blue-900/60 text-blue-300" : "bg-pink-900/60 text-pink-300"
          }`}>
            {speaker.name[0]}
          </div>
        )}
      </div>
      <span className="text-xs text-white/80 font-medium">{speaker.name}</span>
    </div>
  );
}

export function DialogHeader({ dialog }: DialogHeaderProps) {
  const nastaliq = useDialogStore((s) => s.toggles.nastaliq);
  const config = getLanguageConfig(dialog.language_code);
  const langName = getLanguageName(dialog.language_code);
  const gradient = LEVEL_GRADIENTS[dialog.level] ?? "from-slate-600 to-slate-700";
  const badge = LEVEL_BADGES[dialog.level] ?? "bg-slate-500/30 text-slate-300 border-slate-500/30";

  const titleDir = config.direction === "rtl" ? "rtl" : undefined;
  let titleFontClass = "";
  if (config.script === "arabic") {
    titleFontClass = nastaliq ? "font-nastaliq" : "font-arabic";
  }

  const title = dialog.title_english || dialog.slug?.replace(/-[a-f0-9]{6,}$/i, "").split("-").map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ") || "";

  return (
    <div className="mb-6">
      <div className={`aspect-[3/2] bg-gradient-to-br ${gradient} rounded-xl overflow-hidden relative mb-4`}>
        {dialog.thumbnail_url && (
          <img
            src={dialog.thumbnail_url}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <div className="absolute top-3 left-3 right-3 flex items-start justify-between">
          <span className="bg-black/50 backdrop-blur-sm text-white text-xs font-semibold px-2.5 py-1 rounded-lg">
            {langName}
          </span>
          <span className={`text-xs font-bold px-2.5 py-1 rounded-lg border backdrop-blur-sm ${badge}`}>
            {dialog.level}
          </span>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black/70 via-black/30 to-transparent">
          <h1 className="text-white font-bold text-xl leading-snug mb-2">
            {title}
          </h1>
          {dialog.title_target && (
            <h2 dir={titleDir} className={`text-white/80 text-lg leading-snug ${titleFontClass}`}>
              {dialog.title_target}
            </h2>
          )}

          <div className="flex items-center gap-4 mt-4">
            {dialog.speakers.map((_, i) => (
              <SpeakerBubble key={i} dialog={dialog} speakerIndex={i} />
            ))}
          </div>
        </div>
      </div>

      {(dialog.description_en || dialog.introduction) && (
        <div className="bg-th-surface-dim rounded-xl border border-th-border p-5 shadow-xl">
          {dialog.description_en && (
            <p className="text-th-text-2 text-sm leading-relaxed">{dialog.description_en}</p>
          )}
          {dialog.introduction && (
            <p className={`${dialog.description_en ? "mt-3 pt-3 border-t border-th-border-dim" : ""} text-th-text-2 text-sm leading-relaxed`}>
              {dialog.introduction}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
